import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft, ChevronRight, Copy, Trash2, CheckCircle2, CalendarDays,
  Instagram, Music2, Youtube, Facebook, Linkedin, Share2, BookOpen, Film,
} from "lucide-react";

const PLATFORM_ICON = {
  instagram: Instagram,
  tiktok: Music2,
  youtube: Youtube,
  youtube_short: Youtube,
  facebook: Facebook,
  linkedin: Linkedin,
  twitter: Share2,
  blog: BookOpen,
  video: Film,
};

const PLATFORM_COLOR = {
  instagram: "#ec4899",
  tiktok: "#22d3ee",
  youtube: "#ef4444",
  youtube_short: "#f97316",
  facebook: "#3b82f6",
  linkedin: "#0ea5e9",
  twitter: "#a3a3a3",
  blog: "#a78bfa",
  video: "#F5A524",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function sameDay(a, b)   { return a.toDateString() === b.toDateString(); }
function ymd(d) { return d.toISOString().slice(0, 10); }

export default function Calendar() {
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const start = startOfMonth(cursor).toISOString();
      const end = new Date(endOfMonth(cursor).setHours(23, 59, 59)).toISOString();
      const { data } = await api.get("/content/schedule", { params: { start, end } });
      setItems(data);
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [cursor]);

  const goPrev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date());

  // Build 6x7 grid starting Monday
  const first = startOfMonth(cursor);
  const startWeekday = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = endOfMonth(cursor).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();

  const itemsForDay = (day) => items.filter((it) => sameDay(new Date(it.scheduled_at), day));

  const removeItem = async (id) => {
    try {
      await api.delete(`/content/schedule/${id}`);
      toast.success("Removed from calendar");
      setItems((prev) => prev.filter((i) => i.id !== id));
      setSelected(null);
    } catch { toast.error("Delete failed"); }
  };

  const markPublished = async (id) => {
    try {
      const { data } = await api.post(`/content/schedule/${id}/publish-now`);
      toast.success("Marked as published");
      setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
      setSelected(data);
    } catch { toast.error("Update failed"); }
  };

  const copyBody = async (item) => {
    const tags = (item.hashtags || []).map((h) => `#${h}`).join(" ");
    const full = [item.title, "", item.body, "", tags].filter(Boolean).join("\n");
    await navigator.clipboard.writeText(full);
    toast.success("Copied to clipboard");
  };

  const monthLabel = cursor.toLocaleString("en", { month: "long", year: "numeric" });

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1600px]" data-testid="calendar-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-tech text-[10px] text-amber-400 mb-2">CONTENT · CALENDAR</div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold leading-none">
            {monthLabel}
          </h1>
          <p className="text-zinc-400 mt-3 max-w-2xl text-sm">
            Alle geplande blogs, social posts en video&apos;s op één plek. Klik op een dag om de details te zien en kopiëren.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={goPrev}
            variant="outline"
            size="icon"
            data-testid="cal-prev"
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button
            onClick={goToday}
            variant="outline"
            data-testid="cal-today"
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            Vandaag
          </Button>
          <Button
            onClick={goNext}
            variant="outline"
            size="icon"
            data-testid="cal-next"
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((d) => (
          <div key={d} className="font-mono-tech text-[10px] text-zinc-500 text-center py-1">
            {d.toUpperCase()}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-2" data-testid="cal-grid">
        {cells.map((day, idx) => {
          if (!day) {
            return <div key={idx} className="min-h-[110px] bg-[#0a0a0b] border border-white/[0.02] rounded-md" />;
          }
          const dayItems = itemsForDay(day);
          const isToday = sameDay(day, today);
          return (
            <div
              key={idx}
              data-testid={`cal-day-${ymd(day)}`}
              className={`min-h-[110px] bg-[#121214] border rounded-md p-2 flex flex-col gap-1 transition-colors ${
                isToday ? "border-amber-500/60 bg-amber-500/[0.03]" : "border-white/5 hover:border-white/15"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs font-mono-tech ${isToday ? "text-amber-400 font-bold" : "text-zinc-500"}`}>
                  {day.getDate()}
                </div>
                {dayItems.length > 0 && (
                  <div className="text-[9px] font-mono-tech text-amber-400">
                    {dayItems.length}
                  </div>
                )}
              </div>
              {dayItems.slice(0, 3).map((it) => {
                const Icon = PLATFORM_ICON[it.platform] || Share2;
                const color = PLATFORM_COLOR[it.platform] || "#F5A524";
                return (
                  <button
                    key={it.id}
                    onClick={() => setSelected(it)}
                    data-testid={`cal-item-${it.id}`}
                    className="flex items-center gap-1.5 rounded px-1.5 py-1 hover:bg-white/5 transition-colors text-left"
                    style={{ backgroundColor: `${color}22`, border: `1px solid ${color}33` }}
                  >
                    <Icon className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} style={{ color }} />
                    <span className="text-[10px] text-zinc-200 truncate flex-1">
                      {it.title || it.body?.slice(0, 30) || "Untitled"}
                    </span>
                    {it.status === "published" && (
                      <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" strokeWidth={2} />
                    )}
                  </button>
                );
              })}
              {dayItems.length > 3 && (
                <div className="text-[9px] text-zinc-500 text-center">+{dayItems.length - 3} meer</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="mt-6 border border-dashed border-white/10 rounded-lg p-10 text-center">
          <CalendarDays className="w-10 h-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-heading text-xl font-bold tracking-tight">Nog niets gepland</div>
          <div className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
            Genereer content in AI Blog of Social Studio en klik op &quot;Plan in kalender&quot; om het hier te zien.
          </div>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-lg" data-testid="cal-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <div className="font-mono-tech text-[10px] text-amber-400 mb-1">
                  {new Date(selected.scheduled_at).toLocaleString("en", {
                    weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
                  })} · {selected.platform.toUpperCase()} · {selected.language.toUpperCase()}
                </div>
                <DialogTitle className="font-heading text-2xl tracking-tight">
                  {selected.title || "(no title)"}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap text-sm">{selected.body}</p>
                {selected.hashtags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selected.hashtags.map((h, i) => (
                      <span key={i} className="text-xs text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded-full">
                        #{h}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-mono-tech px-2 py-0.5 rounded uppercase ${
                      selected.status === "published"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : selected.status === "cancelled"
                        ? "bg-red-500/20 text-red-400"
                        : "bg-amber-500/20 text-amber-400"
                    }`}
                  >
                    {selected.status}
                  </span>
                </div>
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => copyBody(selected)}
                  data-testid="cal-copy"
                  className="bg-transparent border-white/15 hover:bg-white/5"
                >
                  <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Kopieer
                </Button>
                {selected.status !== "published" && (
                  <Button
                    onClick={() => markPublished(selected.id)}
                    data-testid="cal-publish"
                    className="bg-emerald-500 hover:bg-emerald-600 text-black"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" strokeWidth={2} /> Markeer gepubliceerd
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => removeItem(selected.id)}
                  data-testid="cal-delete"
                  className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Verwijder
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
